import { apiClient } from './client'

export interface AnalyticsFunnelStage {
  key: string
  label: string
  count: number
  conversion: number
}

export interface AnalyticsOverview {
  funnel: AnalyticsFunnelStage[]
  deadlines: {
    overdueNoQuote: number
    overdueOnSales: number
    overdueOnProcurement: number
    lateQuote: number
  }
  timeline: Array<{
    date: string
    created: number
    toProcurement: number
    quotationCreated: number
    ordered: number
  }>
  stageTimes: Array<{ key: string; label: string; avgDays: number }>
  stuck: Array<{
    requestId: string
    requestNumber: string | null
    clientName: string
    status: string
    daysStuck: number
  }>
  /** Всего застрявших заявок (может превышать stuck.length при усечении). */
  stuckTotal: number
  /** «Заявки в отказ» и «В архив» по причинам за период (19.09.2026). */
  closeReasons: {
    rejection: Array<{ code: string | null; label: string; count: number }>
    archive: Array<{ code: string | null; label: string; count: number }>
  }
}

export interface AnalyticsPeriodParams {
  period?: string
  date?: string | null
  from?: string | null
  to?: string | null
}

/** Виджеты вкладки «Обзор»: воронка, срыв дедлайнов, динамика по дням. */
export async function getAnalyticsOverview(
  params: AnalyticsPeriodParams,
): Promise<AnalyticsOverview> {
  const res = await apiClient.get<{ success: true; data: AnalyticsOverview }>(
    '/analytics/overview',
    { params },
  )
  return res.data.data
}

export interface ManagerAnalyticsRow {
  managerId: string
  name: string
  planAmount: number
  factAmount: number
  planPct: number
  akbPlanCount: number
  akbFactCount: number
  totalClients: number
  activeClients: number
  overdueCount: number
  activeLoad: number
}

/** Сводная таблица вкладки «Менеджеры». */
export async function getAnalyticsManagers(
  params: AnalyticsPeriodParams,
): Promise<{ rows: ManagerAnalyticsRow[] }> {
  const res = await apiClient.get<{ success: true; data: { rows: ManagerAnalyticsRow[] } }>(
    '/analytics/managers',
    { params },
  )
  return res.data.data
}

export interface ConversionRow {
  key: string
  total: number
  won: number
  conversion: number
}

export interface ClientAnalyticsRow {
  clientId: string
  name: string
  segment: string
  category: string
  requests: number
  won: number
  conversion: number
}

export interface ClientsAnalytics {
  bySegment: ConversionRow[]
  byCategory: ConversionRow[]
  clients: ClientAnalyticsRow[]
  sleepingCount: number
}

/** Вкладка «Клиенты»: конверсия по сегменту/категории + таблица клиентов. */
export async function getAnalyticsClients(
  params: AnalyticsPeriodParams,
): Promise<ClientsAnalytics> {
  const res = await apiClient.get<{ success: true; data: ClientsAnalytics }>('/analytics/clients', {
    params,
  })
  return res.data.data
}

export interface BuyerAnalyticsRow {
  buyerId: string
  name: string
  quotesCreated: number
  supplierRequests: number
  wonQuotes: number
  winRate: number
  uniqueSuppliers: number
}

/** Вкладка «Закупщики»: сводная таблица. */
export async function getAnalyticsBuyers(
  params: AnalyticsPeriodParams,
): Promise<{ rows: BuyerAnalyticsRow[] }> {
  const res = await apiClient.get<{ success: true; data: { rows: BuyerAnalyticsRow[] } }>(
    '/analytics/buyers',
    { params },
  )
  return res.data.data
}

export interface SupplierAnalyticsRow {
  supplier: string
  requested: number
  answered: number
  responseRate: number
  won: number
  winRate: number
}

/** Вкладка «Поставщики»: реестр-воронка. */
export async function getAnalyticsSuppliers(
  params: AnalyticsPeriodParams,
): Promise<{ rows: SupplierAnalyticsRow[] }> {
  const res = await apiClient.get<{ success: true; data: { rows: SupplierAnalyticsRow[] } }>(
    '/analytics/suppliers',
    { params },
  )
  return res.data.data
}

// ── Вкладка «Сводка» ─────────────────────────────────────────────────────────

export interface SummaryMetric {
  current: number
  previous: number | null
  deltaPct: number | null
}

export interface SummaryDimensionRow {
  key: string
  volume: number
  orderedCount: number
  orderedSum: number
}

/** Клиент когорты периода — для модалки KPI-карточки «Новых клиентов». */
export interface SummaryPeriodClient {
  clientId: string
  name: string
  isNew: boolean
}

export interface SummaryClientRow {
  clientId: string
  name: string
  segment: string
  category: string
  requests: number
  kpSum: number
  orderedSum: number
  won: number
  conversion: number
  isNew: boolean
}

export interface SummaryManagerRow {
  managerId: string
  name: string
  requests: number
  won: number
  conversion: number
  kpSum: number
  orderedSum: number
}

export interface SummaryFunnelStage {
  key: string
  label: string
  count: number
  conversion: number
}

export interface SummaryOutcomes {
  total: number
  inWork: number
  kp: number
  won: number
  lost: number
  archived: number
}

export interface AnalyticsSummary {
  kpi: {
    requests: SummaryMetric
    newClients: SummaryMetric
    returningClients: SummaryMetric
    items: SummaryMetric
    quotedAmount: SummaryMetric
    orderedAmount: SummaryMetric
    conversionRate: SummaryMetric
  }
  outcomes: SummaryOutcomes
  funnel: SummaryFunnelStage[]
  /** Сегменты клиентов (client.segment); volume = заявок когорты. */
  segments: SummaryDimensionRow[]
  /** Сегменты товаров (request_items.category, «Наименование»); volume = позиций. */
  categories: SummaryDimensionRow[]
  matrices: SummaryDimensionRow[]
  brands: SummaryDimensionRow[]
  clients: SummaryClientRow[]
  managers: SummaryManagerRow[]
  /** Все клиенты когорты — для модалки «Новых клиентов». */
  periodClients: SummaryPeriodClient[]
  compared: boolean
}

export interface SummaryFilterValues {
  managerIds?: string[]
  segments?: string[]
  cities?: string[]
  brands?: string[]
  type?: 'TEKUCHKA' | 'PROJECT' | ''
}

export interface SummaryOptions {
  managers: Array<{ id: string; name: string }>
  segments: string[]
  cities: string[]
  brands: string[]
}

function buildSummaryParams(
  period: AnalyticsPeriodParams,
  filters: SummaryFilterValues,
  compare: boolean,
): Record<string, string> {
  const q: Record<string, string> = {}
  if (period.period) q.period = period.period
  if (period.date) q.date = period.date
  if (period.from) q.from = period.from
  if (period.to) q.to = period.to
  if (compare) q.compare = '1'
  if (filters.managerIds?.length) q.managerIds = filters.managerIds.join(',')
  if (filters.segments?.length) q.segments = filters.segments.join(',')
  if (filters.cities?.length) q.cities = filters.cities.join(',')
  if (filters.brands?.length) q.brands = filters.brands.join(',')
  if (filters.type) q.type = filters.type
  return q
}

/** Сводка по заявкам/сотрудникам за период (+ дельты при compare). */
export async function getAnalyticsSummary(
  period: AnalyticsPeriodParams,
  filters: SummaryFilterValues,
  compare: boolean,
): Promise<AnalyticsSummary> {
  const res = await apiClient.get<{ success: true; data: AnalyticsSummary }>('/analytics/summary', {
    params: buildSummaryParams(period, filters, compare),
  })
  return res.data.data
}

/** Списки для фильтров сводки. */
export async function getAnalyticsSummaryOptions(): Promise<SummaryOptions> {
  const res = await apiClient.get<{ success: true; data: SummaryOptions }>(
    '/analytics/summary/options',
  )
  return res.data.data
}

export interface DigestAlert {
  level: 'red' | 'amber'
  text: string
}

/** Сводный «светофор» — главные алёрты по системе за выбранный период. */
export async function getAnalyticsDigest(
  params: AnalyticsPeriodParams,
): Promise<{ alerts: DigestAlert[] }> {
  const res = await apiClient.get<{ success: true; data: { alerts: DigestAlert[] } }>(
    '/analytics/digest',
    { params },
  )
  return res.data.data
}

export interface KpTimelineRow {
  requestId: string
  requestNumber: string | null
  clientName: string
  managerName: string
  status: string
  createdAt: string
  fullyQuotedAt: string | null
  kpCreatedAt: string | null
  kpLastAt: string | null
  kpCount: number
  kpSentAt: string | null
  finalAt: string | null
  isFinal: boolean
  nextActionDueAt: string | null
  nextActionNote: string | null
  lastDoneAt: string | null
  lastDoneNote: string | null
  totalDays: number
}

export interface KpTimelineResponse {
  rows: KpTimelineRow[]
  total: number
  truncated: boolean
}

/** Сводный КП-таймлайн заявок по менеджерам (создана → … → финал). */
export async function getAnalyticsKpTimeline(
  params: AnalyticsPeriodParams,
): Promise<KpTimelineResponse> {
  const res = await apiClient.get<{ success: true; data: KpTimelineResponse }>(
    '/analytics/kp-timeline',
    { params },
  )
  return res.data.data
}
